import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import moment from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { HotelDateTimePicker as DateTimePicker } from '../../shared/components/forms/HotelDatePicker';

const DEFAULT_ACCENT = '#4ce1b6';

export default function ActualStayDateTimeEditDialog({
  open,
  onClose,
  title,
  subtitle,
  scheduledHint,
  value,
  onSave,
  saving = false,
  isDark = true,
  accent = DEFAULT_ACCENT,
  kind = 'checkin',
}) {
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (open) {
      setDraft(value && moment.isMoment(value) && value.isValid() ? value.clone() : moment());
    }
  }, [open, value]);

  const Icon = kind === 'checkout' ? LogoutIcon : LoginIcon;

  const handleSave = () => {
    if (!draft || !draft.isValid()) return;
    onSave?.(draft.clone());
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha(accent, 0.2)}`,
          background: isDark
            ? `linear-gradient(160deg, #2a2a31 0%, #232329 55%, #1c1c22 100%)`
            : `linear-gradient(160deg, #ffffff 0%, ${alpha(accent, 0.05)} 100%)`,
          boxShadow: isDark
            ? `0 24px 48px ${alpha('#000', 0.45)}`
            : `0 20px 40px ${alpha('#0f766e', 0.12)}`,
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          background: `linear-gradient(135deg, ${alpha(accent, isDark ? 0.22 : 0.16)} 0%, ${alpha(accent, 0.04)} 100%)`,
          borderBottom: `1px solid ${alpha(accent, 0.22)}`,
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${accent} 0%, ${alpha(accent, 0.75)} 100%)`,
              boxShadow: `0 8px 20px ${alpha(accent, 0.35)}`,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ color: '#000', fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isDark ? '#f5f5f5' : '#0f172a' }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="caption" sx={{ color: isDark ? alpha('#fff', 0.65) : '#64748b', display: 'block', mt: 0.25 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          <IconButton size="small" onClick={onClose} disabled={saving} sx={{ color: isDark ? '#aaa' : '#666', mt: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        {scheduledHint ? (
          <Box
            sx={{
              mb: 2,
              p: 1.25,
              borderRadius: 2,
              border: `1px solid ${alpha(accent, 0.2)}`,
              background: isDark ? alpha('#fff', 0.04) : alpha(accent, 0.06),
            }}
          >
            <Typography variant="caption" sx={{ color: isDark ? alpha('#fff', 0.55) : '#64748b', fontWeight: 600 }}>
              Booked stay
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#0f172a', mt: 0.25 }}>
              {scheduledHint}
            </Typography>
          </Box>
        ) : null}

        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DateTimePicker
            label="Date & time"
            value={draft}
            onChange={(next) => {
              if (next && next.isValid()) setDraft(next);
            }}
            ampm
            slotProps={{
              textField: {
                fullWidth: true,
                sx: {
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: isDark ? alpha('#fff', 0.04) : '#fff',
                    '& fieldset': { borderColor: isDark ? alpha('#fff', 0.12) : alpha('#000', 0.12) },
                    '&:hover fieldset': { borderColor: accent },
                    '&.Mui-focused fieldset': { borderColor: accent, borderWidth: 2 },
                  },
                },
              },
            }}
          />
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          sx={{
            flex: 1,
            borderRadius: 2,
            fontWeight: 700,
            borderColor: isDark ? alpha('#fff', 0.15) : alpha('#000', 0.15),
            color: isDark ? '#ddd' : '#444',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !draft?.isValid?.()}
          variant="contained"
          sx={{
            flex: 1,
            borderRadius: 2,
            fontWeight: 800,
            color: '#000',
            background: `linear-gradient(135deg, ${accent} 0%, ${alpha(accent, 0.85)} 100%)`,
            boxShadow: `0 8px 20px ${alpha(accent, 0.35)}`,
            '&:hover': {
              background: `linear-gradient(135deg, ${accent} 0%, ${accent} 100%)`,
            },
          }}
        >
          {saving ? <CircularProgress size={22} sx={{ color: '#000' }} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
